import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { inviteTeamMember } from '@/api/misc';
import { qk } from '@/lib/query';
import { useSession } from '@/hooks/useSession';
import { useTeamInvites, useTeamMembers } from '@/hooks/useSettings';
import { ApiError } from '@/lib/api';
import { Screen } from '@m/ui/Screen';
import { Avatar, Group, Row } from '@m/ui/List';
import { BarButton, Segmented, SkeletonList } from '@m/ui/Controls';
import { Sheet } from '@m/ui/Sheet';
import { FieldRow } from '../clients/forms';

/** Who is on the team. Admins can invite; roles and removal stay on the desktop. */
export function TeamScreen() {
  const session = useSession();
  const teamId = session.team?.id;
  const members = useTeamMembers(teamId);
  const invites = useTeamInvites(session.isAdmin ? teamId : undefined);
  const [inviting, setInviting] = useState(false);

  return (
    <Screen
      title="Team"
      back="/more"
      backLabel="More"
      actions={
        session.isAdmin && (
          <BarButton aria-label="Invite someone" onClick={() => setInviting(true)}>
            <UserPlus className="size-6" />
          </BarButton>
        )
      }
    >
      {members.isPending ? (
        <SkeletonList rows={3} />
      ) : (
        <Group header={session.team?.name ?? 'Team'} headerRight={`${members.data?.length ?? 0}`}>
          {(members.data ?? []).map((m) => (
            <Row
              key={m.id}
              leading={<Avatar id={m.userId} name={m.name ?? m.email} />}
              title={m.name ?? m.email ?? 'Member'}
              subtitle={m.name ? m.email : undefined}
              trailing={<RoleBadge role={m.role} />}
            />
          ))}
        </Group>
      )}
      {(invites.data?.length ?? 0) > 0 && (
        <Group header="Invited">
          {invites.data!.map((i) => (
            <Row key={i.id} title={i.email} subtitle="Invite sent" trailing={<RoleBadge role={i.role} />} />
          ))}
        </Group>
      )}
      <InviteSheet key={String(inviting)} open={inviting} onClose={() => setInviting(false)} teamId={teamId} />
    </Screen>
  );
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-flex h-[22px] items-center rounded-full px-2 text-[12px] font-medium capitalize ${
        role === 'admin' ? 'bg-[var(--accent-soft)] text-[var(--accent)]' : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
      }`}
    >
      {role}
    </span>
  );
}

function InviteSheet({ open, onClose, teamId }: { open: boolean; onClose: () => void; teamId: string | undefined }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const invite = useMutation({
    mutationFn: () => inviteTeamMember(teamId!, { email: email.trim(), role }),
    onSuccess() {
      void qc.invalidateQueries({ queryKey: qk.teamInvites });
      void qc.invalidateQueries({ queryKey: qk.subscription });
      toast.success(`Invite sent to ${email.trim()}`);
      onClose();
    },
    onError(err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the invite.');
    }
  });
  const valid = /.+@.+\..+/.test(email.trim()) && !!teamId;
  return (
    <Sheet
      open={open}
      onClose={onClose}
      dirty={email !== ''}
      title="Invite"
      leading={<BarButton onClick={onClose}>Cancel</BarButton>}
      trailing={
        <BarButton className="font-semibold" disabled={!valid || invite.isPending} onClick={() => invite.mutate()}>
          Send
        </BarButton>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="overflow-hidden rounded-2xl bg-[var(--surface)]">
          <FieldRow label="Email" type="email" inputMode="email" autoCapitalize="off" autoFocus placeholder="name@firm.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Segmented
          value={role}
          onChange={setRole}
          options={[
            { value: 'member', label: 'Member' },
            { value: 'admin', label: 'Admin' }
          ]}
        />
        <p className="px-4 text-caption text-[var(--text-muted)]">
          {role === 'admin' ? 'Admins can invoice, see reports and change settings.' : 'Members log their own time.'} An invite holds a seat until it is used or revoked.
        </p>
      </div>
    </Sheet>
  );
}
